import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Truck, BarChart3, MapPin, Clock, Package, Route } from 'lucide-react';
import '../styles/Pages.css';
import { Link } from 'react-router-dom';

const Logistics = () => {
  const features = [
    {
      icon: <Truck size={24} />,
      title: 'Gestión de Flota Logística',
      description: 'Control completo de vehículos de transporte, camiones y unidades de reparto.'
    },
    {
      icon: <Route size={24} />,
      title: 'Optimización de Rutas',
      description: 'Algoritmos avanzados para calcular las rutas más eficientes y reducir costos de operación.'
    },
    {
      icon: <Package size={24} />,
      title: 'Seguimiento de Entregas',
      description: 'Monitoreo en tiempo real del estado y ubicación de cada entrega desde origen hasta destino.'
    },
    {
      icon: <MapPin size={24} />,
      title: 'Geocercas Logísticas',
      description: 'Definición de zonas para almacenes, centros de distribución y puntos de entrega.'
    },
    {
      icon: <Clock size={24} />,
      title: 'Control de Tiempos',
      description: 'Medición precisa de tiempos de carga, descarga y entrega para optimizar operaciones.'
    },
    {
      icon: <BarChart3 size={24} />,
      title: 'Análisis de Rendimiento',
      description: 'Informes detallados sobre eficiencia de rutas, tiempos de entrega y costos operativos.'
    }
  ];

  const benefits = [
    {
      title: 'Reducción de Costos Operativos',
      items: [
        'Optimización de rutas para menor consumo de combustible',
        'Reducción de horas extra innecesarias',
        'Disminución de mantenimientos correctivos',
        'Mejor aprovechamiento de recursos'
      ]
    },
    {
      title: 'Mayor Eficiencia en Entregas',
      items: [
        'Planificación inteligente de rutas',
        'Reducción de tiempos de espera',
        'Mejor coordinación entre conductores y almacenes',
        'Respuesta rápida ante imprevistos'
      ]
    },
    {
      title: 'Mejora en Servicio al Cliente',
      items: [
        'Información precisa sobre tiempos de entrega',
        'Notificaciones automáticas de estado',
        'Prueba de entrega digital',
        'Mayor satisfacción del cliente'
      ]
    }
  ];

  return (
    <div className="page-container">
      <Header scrolled={true} />

      <main className="page-content">
        <section className="page-hero">
          <div className="container">
            <h1>Soluciones Logísticas</h1>
            <p>Optimiza tus operaciones de transporte y distribución con VillaTracking</p>
          </div>
        </section>

        <section className="page-section">
          <div className="container">
            <div className="page-grid">
              <div className="page-text">
                <h2>Transformando la Logística con Tecnología</h2>
                <p>En el competitivo sector logístico, la eficiencia y precisión son factores críticos para el éxito. VillaTracking ofrece soluciones especializadas para empresas de transporte y distribución que permiten optimizar rutas, reducir costos operativos y mejorar los tiempos de entrega.</p>
                <p>Nuestra plataforma integra el seguimiento en tiempo real de vehículos con potentes herramientas de planificación y análisis, proporcionando una visibilidad completa de toda la cadena logística.</p>
                <Link to="/#contact" className="btn btn-primary btn-lg">Solicitar Demo</Link>
              </div>
              <div className="page-image">
                <img src="img/logistics1.jpeg" alt="Logística VillaTracking" />
              </div>
            </div>
          </div>
        </section>

        <section className="page-section page-section-alt">
          <div className="container">
            <h2 className="text-center">Características Especializadas para Logística</h2>
            <div className="features-grid-3">
              {features.map((feature, index) => (
                <div className="feature-card" key={index}>
                  <div className="feature-icon">
                    {feature.icon}
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="page-section">
          <div className="container">
            <div className="page-grid">
              <div className="page-image">
                <img src="img/logistics2.webp" alt="Optimización de Rutas" />
              </div>
              <div className="page-text">
                <h2>Optimización Inteligente de Rutas</h2>
                <p>Nuestro sistema de optimización de rutas analiza múltiples variables como tráfico en tiempo real, restricciones de circulación, horarios de entrega y características de los vehículos para calcular las rutas más eficientes.</p>
                <p>Los beneficios son inmediatos: reducción de kilómetros recorridos, menor consumo de combustible, optimización de tiempos y aumento en la capacidad de entregas diarias.</p>
                <ul className="feature-list">
                  <li>Planificación automática de rutas óptimas</li>
                  <li>Consideración de ventanas horarias de entrega</li>
                  <li>Adaptación en tiempo real a condiciones de tráfico</li>
                  <li>Balanceo de carga entre vehículos</li>
                  <li>Histórico de rutas para análisis y mejora continua</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="page-section page-section-alt">
          <div className="container">
            <h2 className="text-center">Beneficios para Empresas Logísticas</h2>
            <div className="benefits-columns">
              {benefits.map((benefit, index) => (
                <div className="benefit-column" key={index}>
                  <h3>{benefit.title}</h3>
                  <ul>
                    {benefit.items.map((item, itemIndex) => (
                      <li key={itemIndex}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="page-section">
          <div className="container">
            <div className="case-study">
              <h2>Caso de Éxito</h2>
              <div className="case-study-content">
                <div className="case-study-text">
                  <h3>Distribuidora Nacional</h3>
                  <p className="case-study-stats">
                    <span>28% reducción en costos de combustible</span>
                    <span>35% mejora en tiempos de entrega</span>
                    <span>98% precisión en estimaciones de llegada</span>
                  </p>
                  <p>Distribuidora Nacional, una empresa con más de 50 vehículos de reparto, implementó VillaTracking para optimizar sus operaciones logísticas. En los primeros seis meses, lograron reducir significativamente sus costos operativos mientras mejoraban la satisfacción de sus clientes gracias a entregas más puntuales y mejor comunicación.</p>
                  <p>"VillaTracking transformó nuestra operación logística. Ahora tenemos control total sobre nuestra flota y podemos ofrecer información precisa a nuestros clientes sobre el estado de sus entregas." - María Fernández, Gerente de Logística</p>
                </div>
                <div className="case-study-image">
                  <img src="img/logistics3.jpeg" alt="Caso de Éxito" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="page-cta">
          <div className="container">
            <h2>Optimiza tus operaciones logísticas hoy mismo</h2>
            <p>Descubre cómo VillaTracking puede transformar tu empresa de logística</p>
            <Link to="/#contact" className="btn btn-primary btn-lg">Solicitar Demo</Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Logistics;